'use client';

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import type {
  CourtProfile,
  InitialRequestEnvelope,
  InitialResponseV5,
  PreferredCourt,
  UserAuth,
  UserNotificationPreference,
  UserProfile,
} from '@/types/initial-request';
import { trackEvent } from '@/lib/analytics';
import type { MyScheduleSnapshot, RealTimeSnapshot } from '@/lib/bootstrap';
import { parseRoles } from '@/lib/roles';

export type CourtDirectoryEntry = {
  courtId: number;
  name: string;
  shortName?: string;
};

type BootstrapState = {
  envelope: InitialRequestEnvelope | null;
  loadedAt: number | null;
};

type AppContextValue = {
  bootstrap: BootstrapState;
  setBootstrapData: (payload: InitialRequestEnvelope) => void;
  clearBootstrap: () => void;
  refreshBootstrap: () => Promise<void>;
  profile: UserProfile | undefined;
  userAuth: UserAuth | undefined;
  roles: string[];
  hasRole: (role: string) => boolean;
  hasAnyRole: (...roles: string[]) => boolean;
  hasAllRoles: (...roles: string[]) => boolean;
  loginIdentifierType: 'email' | 'phone' | null;
  updateProfile: (profile: UserProfile) => void;
  updateSettings: (settings: {
    statusGeneral?: number;
    privacyDefaultAudience?: number;
    privacyFollower?: number;
    notifications?: Partial<UserNotificationPreference>;
  }) => void;
  updateVenues: (venues: {
    primaryCourt?: number | null;
    preferredCourts?: PreferredCourt[];
    preferredCourtsProfile?: CourtProfile[];
    myCourtsProfile?: CourtProfile[];
  }) => void;
  refreshRealTimeData: (options?: { courtIds?: number[] }) => Promise<void>;
  refreshMyScheduleData: () => Promise<void>;
  courtDirectory: CourtDirectoryEntry[];
};

const DEFAULT_VALUE: AppContextValue = {
  bootstrap: { envelope: null, loadedAt: null },
  setBootstrapData: () => undefined,
  clearBootstrap: () => undefined,
  refreshBootstrap: async () => undefined,
  profile: undefined,
  userAuth: undefined,
  roles: [],
  hasRole: () => false,
  hasAnyRole: () => false,
  hasAllRoles: () => false,
  loginIdentifierType: null,
  updateProfile: () => undefined,
  updateSettings: () => undefined,
  updateVenues: () => undefined,
  refreshRealTimeData: async () => undefined,
  refreshMyScheduleData: async () => undefined,
  courtDirectory: [],
};

const AppContext = createContext<AppContextValue>(DEFAULT_VALUE);

export function AppContextProvider({ children }: { children: React.ReactNode }) {
  const [bootstrap, setBootstrap] = useState<BootstrapState>({ envelope: null, loadedAt: null });
  const setBootstrapPending = useRef(false);

  const roles = useMemo(() => {
    const rawRoles = bootstrap.envelope?.data.userGlobalRolesView?.roles;
    return parseRoles(rawRoles);
  }, [bootstrap.envelope]);

  const rolesSet = useMemo(() => new Set(roles), [roles]);

  const hasRole = useCallback(
    (role: string) => {
      if (!role) return false;
      return rolesSet.has(role.toLowerCase().trim());
    },
    [rolesSet],
  );

  const hasAnyRole = useCallback(
    (...roleList: string[]) => {
      if (!roleList.length) return false;
      return roleList.some((role) => rolesSet.has(role.toLowerCase().trim()));
    },
    [rolesSet],
  );

  const hasAllRoles = useCallback(
    (...roleList: string[]) => {
      if (!roleList.length) return false;
      return roleList.every((role) => rolesSet.has(role.toLowerCase().trim()));
    },
    [rolesSet],
  );

  const setBootstrapData = useCallback((payload: InitialRequestEnvelope) => {
    if (setBootstrapPending.current) return;
    setBootstrapPending.current = true;
    setBootstrap((current) => {
      if (current.envelope) {
        setBootstrapPending.current = false;
        return current;
      }
      trackEvent('bootstrap_hydrated', { status: payload.message });
      setBootstrapPending.current = false;
      return { envelope: enrichEnvelope(payload), loadedAt: Date.now() };
    });
  }, []);

  const clearBootstrap = useCallback(() => {
    setBootstrap({ envelope: null, loadedAt: null });
    setBootstrapPending.current = false;
    trackEvent('bootstrap_cleared');
  }, []);

  const refreshBootstrap = useCallback(async () => {
    const response = await fetch('/api/bootstrap', { method: 'GET', cache: 'no-store' });
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || 'Unable to refresh bootstrap.');
    }
    const payload = (await response.json()) as { data?: InitialRequestEnvelope };
    if (!payload?.data) return;
    setBootstrap({ envelope: enrichEnvelope(payload.data), loadedAt: Date.now() });
  }, []);

  const updateProfile = useCallback((nextProfile: UserProfile) => {
    setBootstrap((current) => {
      if (!current.envelope) return current;

      const nextEnvelope: InitialRequestEnvelope = {
        ...current.envelope,
        data: {
          ...current.envelope.data,
          userProfile: nextProfile,
        },
      };

      return {
        ...current,
        envelope: enrichEnvelope(nextEnvelope),
      };
    });
  }, []);

  const updateSettings = useCallback(
    (settings: {
      statusGeneral?: number;
      privacyDefaultAudience?: number;
      privacyFollower?: number;
      notifications?: Partial<UserNotificationPreference>;
    }) => {
      setBootstrap((current) => {
        if (!current.envelope) return current;

        const nextEnvelope: InitialRequestEnvelope = {
          ...current.envelope,
          data: {
            ...current.envelope.data,
            userProfile: current.envelope.data.userProfile
              ? {
                  ...current.envelope.data.userProfile,
                  ...(settings.statusGeneral !== undefined ? { statusGeneral: settings.statusGeneral } : null),
                  ...(settings.privacyDefaultAudience !== undefined
                    ? { privacyDefaultAudience: settings.privacyDefaultAudience }
                    : null),
                  ...(settings.privacyFollower !== undefined ? { privacyFollower: settings.privacyFollower } : null),
                }
              : current.envelope.data.userProfile,
            userNotificationPreference: current.envelope.data.userNotificationPreference
              ? {
                  ...current.envelope.data.userNotificationPreference,
                  ...(settings.notifications ?? {}),
                }
              : settings.notifications
                ? ({
                    username: current.envelope.data.userProfile?.username ?? '',
                    ...settings.notifications,
                  } as UserNotificationPreference)
                : current.envelope.data.userNotificationPreference,
          },
        };

        return {
          ...current,
          envelope: enrichEnvelope(nextEnvelope),
        };
      });
    },
    [],
  );

  const updateVenues = useCallback(
    (venues: {
      primaryCourt?: number | null;
      preferredCourts?: PreferredCourt[];
      preferredCourtsProfile?: CourtProfile[];
      myCourtsProfile?: CourtProfile[];
    }) => {
      setBootstrap((current) => {
        if (!current.envelope) return current;

        const currentData = current.envelope.data;
        const nextData: InitialResponseV5 = {
          ...currentData,
          preferredCourts: venues.preferredCourts ?? currentData.preferredCourts,
          preferredCourtsProfile: venues.preferredCourtsProfile ?? currentData.preferredCourtsProfile,
          myCourtsProfile: venues.myCourtsProfile ?? currentData.myCourtsProfile,
          userProfile: currentData.userProfile
            ? {
                ...currentData.userProfile,
                ...(venues.primaryCourt !== undefined ? { primaryCourt: venues.primaryCourt ?? undefined } : null),
              }
            : currentData.userProfile,
        };

        return {
          ...current,
          envelope: enrichEnvelope({
            ...current.envelope,
            data: nextData,
          }),
        };
      });
    },
    [],
  );

  const refreshRealTimeData = useCallback(
    async (options?: { courtIds?: number[] }) => {
      const response = await fetch('/api/real-time/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        cache: 'no-store',
        body: JSON.stringify({
          courtIds: options?.courtIds && options.courtIds.length ? options.courtIds : undefined,
        }),
      });

      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error || 'Unable to refresh real-time data.');
      }

      const payload = (await response.json()) as { data?: RealTimeSnapshot };
      const snapshot = payload.data;
      if (!snapshot) return;

      setBootstrap((current) => {
        if (!current.envelope) return current;

        const nextData: InitialResponseV5 = {
          ...current.envelope.data,
        };

        if (snapshot.scheduledPlays !== undefined) {
          nextData.scheduledPlays = snapshot.scheduledPlays ?? [];
        }
        if (snapshot.summary !== undefined) {
          nextData.whosPlayings = snapshot.summary ?? [];
        }
        if (snapshot.detailPlayers !== undefined) {
          nextData.whosPlayingDetails = snapshot.detailPlayers ?? [];
        }
        if (snapshot.hostInvitations !== undefined) {
          nextData.hostInvitations = snapshot.hostInvitations ?? [];
        }
        if (snapshot.questions !== undefined) {
          nextData.scheduleConfirmationQuestions = snapshot.questions ?? [];
        }
        if (snapshot.playersDetailRanks !== undefined) {
          nextData.playersDetailRanks = snapshot.playersDetailRanks ?? [];
        }

        const nextEnvelope = enrichEnvelope({
          ...current.envelope,
          data: nextData,
        });

        return {
          ...current,
          envelope: nextEnvelope,
        };
      });
    },
    [],
  );

  const refreshMyScheduleData = useCallback(async () => {
    const response = await fetch('/api/my-play-schedule', {
      method: 'GET',
      cache: 'no-store',
    });

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string };
      throw new Error(body.error || 'Unable to refresh schedule data.');
    }

    const payload = (await response.json()) as { data?: MyScheduleSnapshot };
    const snapshot = payload.data;
    if (!snapshot) return;

    setBootstrap((current) => {
      if (!current.envelope) return current;

      const username =
        current.envelope.data.userAuth?.username?.trim() ?? current.envelope.data.userProfile?.username?.trim() ?? '';
      const nextData: InitialResponseV5 = {
        ...current.envelope.data,
      };

      if (snapshot.scheduledPlays !== undefined) {
        const existing = current.envelope.data.scheduledPlays ?? [];
        const withoutUser = username ? existing.filter((play) => play.username?.trim() !== username) : existing;
        nextData.scheduledPlays = [...withoutUser, ...(snapshot.scheduledPlays ?? [])];
        nextData.mySchedulePlays = snapshot.scheduledPlays ?? [];
      }

      if (snapshot.hostInvitations !== undefined) {
        nextData.hostInvitations = snapshot.hostInvitations ?? [];
      }

      if (snapshot.questions !== undefined) {
        nextData.scheduleConfirmationQuestions = snapshot.questions ?? [];
      }

      if (snapshot.userActivityUsageMonthly !== undefined) {
        nextData.userActivityUsageMonthly = snapshot.userActivityUsageMonthly ?? [];
      }

      const nextEnvelope = enrichEnvelope({
        ...current.envelope,
        data: nextData,
      });

      return {
        ...current,
        envelope: nextEnvelope,
      };
    });
  }, []);

  const value = useMemo<AppContextValue>(() => {
    const data: InitialResponseV5 | undefined = bootstrap.envelope?.data;
    const profile = data?.userProfile;
    const userAuth = data?.userAuth;

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const rawUsername = userAuth?.username?.trim();
    const loginIdentifierType = rawUsername ? (emailPattern.test(rawUsername) ? 'email' : 'phone') : null;

    const courtDirectory = 
    buildCourtDirectory(data);

    return {
      bootstrap,
      setBootstrapData,
      clearBootstrap,
      refreshBootstrap,
      profile,
      userAuth,
      roles,
      hasRole,
      hasAnyRole,
      hasAllRoles,
      loginIdentifierType,
      updateProfile,
      updateSettings,
      updateVenues,
      refreshRealTimeData,
      refreshMyScheduleData,
      courtDirectory,
    };
  }, [
    bootstrap,
    setBootstrapData,
    clearBootstrap,
    refreshBootstrap,
    roles,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    updateProfile,
    updateSettings,
    updateVenues,
    refreshRealTimeData,
    refreshMyScheduleData,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function enrichEnvelope(envelope: InitialRequestEnvelope): InitialRequestEnvelope {
  const username =
    envelope.data.userAuth?.username?.trim() ?? envelope.data.userProfile?.username?.trim() ?? '';
  const scheduledPlays = envelope.data.scheduledPlays ?? [];
  const mySchedulePlays =
    username && scheduledPlays.length
      ? scheduledPlays.filter((play) => play.username?.trim() === username)
      : [];

  return {
    ...envelope,
    data: {
      ...envelope.data,
      mySchedulePlays,
    },
  };
}

function buildCourtDirectory(data?: InitialResponseV5): CourtDirectoryEntry[] {
  if (!data) return [];
  const directory: CourtDirectoryEntry[] = [];
  const seen = new Set<number>();

  const profiles = [
    ...(data.preferredCourtsProfile ?? []),
    ...(data.myCourtsProfile ?? []),
  ];

  const getProfile = (courtId: number | undefined | null) => {
    if (!courtId) return undefined;
    return profiles.find((court) => court.courtId === courtId);
  };

  const pushCourt = (courtId?: number | null) => {
    if (!courtId || seen.has(courtId)) return;
    seen.add(courtId);
    const profile = getProfile(courtId);
    directory.push({
      courtId,
      name: profile?.courtName?.trim() || profile?.courtShortName?.trim() || `Court ${courtId}`,
      shortName: profile?.courtShortName?.trim(),
    });
  };

  const primary = data.userProfile?.primaryCourt ?? null;
  if (primary) {
    pushCourt(primary);
  }

  (data.preferredCourts ?? []).forEach((court) => pushCourt(court.courtId));
  profiles.forEach((profile) => pushCourt(profile.courtId));

  return directory;
}

export function useAppContext() {
  return useContext(AppContext);
}
